import {useEffect, useRef, useState} from 'react';
import {isServer} from 'coreUtils/utils';
import {FraudSDK, FraudProvider, type FraudSDKOptions} from '@payrails/fraud-sdk';
import {isPayrailsRiskSDKActive} from 'coreUtils/payrailsRisk';
import {getRiskifiedSessionId} from 'coreUtils/riskified';
import {useCustomerMgr, useLocalesMgr, useConfigurations, usePathname} from 'coreHooks';
import {useBasket} from 'brandHooks';
import {getConfig} from 'coreUtils/utils';
import {buildOrderSessionId, getSignifydApi, SIGNIFYD_SCRIPT_ID} from './useSignifydFingerprint';

type LoadState = {
	key: string;
	status: 'idle' | 'loading' | 'loaded';
};

let loadState: LoadState = {key: '', status: 'idle'};

const SID_POLL_INTERVAL_MS = 250;
const SID_MAX_ATTEMPTS = 40;
const RISKIFIED_BEACON_SRC_PREFIX = 'https://beacon.riskified.com';

/**
 * Removes the Riskified scripts injected for a previous SFCC session, so only
 * the Beacon matching the current `sid` stays mounted. The Fraud SDK caches
 * scripts by id (which embeds the storeDomain, including the `sid`) and never
 * cleans up a storeDomain that changed.
 */
const removeStaleRiskifiedScripts = (activeStoreDomain: string): void => {
	document.querySelectorAll(`script[data-provider="${FraudProvider.RISKIFIED}"]`).forEach((script) => {
		if (script.id !== `${FraudProvider.RISKIFIED}-${activeStoreDomain}`) {
			script.remove();
		}
	});

	document.querySelectorAll(`script[src^="${RISKIFIED_BEACON_SRC_PREFIX}"]`).forEach((script) => {
		const src = script.getAttribute('src') || '';

		if (!src.includes(activeStoreDomain)) {
			script.remove();
		}
	});
};

/**
 * usePayrailsFraudSDK custom hook.
 *
 * Loads the Payrails Web Fraud SDK abstraction on top of the Signifyd and
 * Riskified fraud providers, replacing the native device-fingerprint script
 * (`useSignifydFingerprint`) and the Riskified beacon
 * (`useLoadRiskifiedScript`).
 *
 * Activation is governed by the `payrailsRiskSDK` flag inside the
 * `payrailsRiskMigrationConfig` site preference: when `true` on any rule, the
 * Payrails Fraud SDK is loaded app-wide (since fingerprinting starts at app
 * load, before a payment method is chosen on the basket). When the flag is
 * `false` or absent, this hook is a no-op and the original Signifyd/Riskified
 * scripts keep running (see the early-return guards added to those hooks).
 *
 * The provider ids are retrieved the same way as in the native hooks:
 *  - Signifyd: the orderSessionId is built by `buildOrderSessionId`
 *    (mirrors Signifyd.prototype.getOrderSessionId server-side).
 *  - Riskified: the SFCC session id is read from the `sid` cookie once the
 *    basket (and therefore the storefront session) is loaded, and re-checked
 *    when the session context changes, polling like `useLoadRiskifiedScript`
 *    until it appears. It is appended to the `riskifiedDomain` site
 *    preference, so the Beacon URL matches the native
 *    `https://beacon.riskified.com?shop=${storeDomain}&sid=${sid}=`.
 *
 * The Fraud SDK caches scripts by id and never updates them, so the native
 * hook behaviors are replicated here:
 *  - a basket change re-binds the Signifyd device fingerprint to the new
 *    orderSessionId through the Signifyd JS API (like `useSignifydFingerprint`);
 *  - a `sigScriptError` "No session ID found" recovers with `init` +
 *    `pageView` (like `useSignifydFingerprint`);
 *  - navigations report Riskified pageviews through `RISKX.go` (like
 *    `useLoadRiskifiedScript`);
 *  - a `sid` change reloads the Beacon and removes the stale session scripts.
 *
 * @see https://docs.payrails.com/docs/payrails-web-fraud-sdk.md
 */
const usePayrailsFraudSDK = () => {
	const basket = useBasket();
	const localesMgr = useLocalesMgr();
	const customerMgr = useCustomerMgr();
	const customer = customerMgr.getCurrentCustomer();
	const pathname = usePathname();
	const configurationsMgr = useConfigurations();
	const config = getConfig();
	const contextAvailable = customerMgr.isContextAvailable();
	const payrailsRiskSDKActive = isPayrailsRiskSDKActive(configurationsMgr.get('sitePreferences.payrailsRiskMigrationConfig'));
	const riskifiedDomain = configurationsMgr.get('sitePreferences.riskifiedDomain') as string | undefined;
	const languageCode = localesMgr.getCurrentLocale()?.getLanguageCode();
	const {instanceHostname, instanceProtocol} = config.app;
	const orderSessionId = languageCode && basket.basketId
		? buildOrderSessionId(instanceProtocol, instanceHostname, languageCode, basket.basketId)
		: undefined;
	const [riskifiedSessionId, setRiskifiedSessionId] = useState<string | null>(null);
	const [riskifiedBeaconReady, setRiskifiedBeaconReady] = useState(false);
	const boundSignifydSessionIdRef = useRef<string | undefined>(undefined);
	const orderSessionIdRef = useRef<string | undefined>(orderSessionId);
	const riskifiedStoreDomain = riskifiedDomain && riskifiedSessionId
		? `${riskifiedDomain}&sid=${riskifiedSessionId}=`
		: riskifiedDomain;
	const loadKey = JSON.stringify([basket.basketId, orderSessionId, riskifiedStoreDomain]);

	useEffect(() => {
		if (isServer || !payrailsRiskSDKActive || !riskifiedDomain || !basket.loaded) {
			return;
		}

		const sessionId = getRiskifiedSessionId();

		if (sessionId) {
			setRiskifiedSessionId(sessionId);

			return;
		}

		let attempts = 0;
		const interval = setInterval(() => {
			const nextSessionId = getRiskifiedSessionId();
			attempts += 1;

			if (nextSessionId) {
				clearInterval(interval);
				setRiskifiedSessionId(nextSessionId);
			} else if (attempts >= SID_MAX_ATTEMPTS) {
				clearInterval(interval);
				console.error(`[Riskified] SID cookie not available after ${SID_MAX_ATTEMPTS} attempts (${SID_MAX_ATTEMPTS * SID_POLL_INTERVAL_MS}ms)`);
				setRiskifiedSessionId('');
			}
		}, SID_POLL_INTERVAL_MS);

		return () => clearInterval(interval);
	}, [basket.loaded, contextAvailable, customer?.isRegistered, payrailsRiskSDKActive, riskifiedDomain]);

	useEffect(() => {
		if (isServer || !payrailsRiskSDKActive) {
			return;
		}

		const handleBeaconReady = () => setRiskifiedBeaconReady(true);

		document.addEventListener('rskx_ready', handleBeaconReady);

		if (global.RISKX && typeof global.RISKX.go === 'function') {
			handleBeaconReady();
		}

		return () => document.removeEventListener('rskx_ready', handleBeaconReady);
	}, [payrailsRiskSDKActive]);

	useEffect(() => {
		if (!isServer && riskifiedBeaconReady && global.RISKX && typeof global.RISKX.go === 'function') {
			global.RISKX.go(pathname);
		}
	}, [pathname, riskifiedBeaconReady]);

	useEffect(() => {
		orderSessionIdRef.current = orderSessionId;

		if (isServer || !payrailsRiskSDKActive || !orderSessionId) {
			return;
		}

		if (boundSignifydSessionIdRef.current === orderSessionId) {
			return;
		}

		const existingTag = document.getElementById(SIGNIFYD_SCRIPT_ID);

		if (!existingTag) {
			return;
		}

		boundSignifydSessionIdRef.current = orderSessionId;
		existingTag.setAttribute('data-order-session-id', orderSessionId);
		getSignifydApi()?.init({sessionId: orderSessionId});
	}, [orderSessionId, payrailsRiskSDKActive]);

	useEffect(() => {
		if (isServer || !contextAvailable || !basket.loaded || !payrailsRiskSDKActive) {
			return;
		}

		if (riskifiedDomain && riskifiedSessionId === null) {
			return;
		}

		if (loadState.key === loadKey && loadState.status !== 'idle') {
			return;
		}

		const providers: FraudSDKOptions = [
			{provider: FraudProvider.SIGNIFYD, ...(orderSessionId ? {orderSessionId} : {})},
		];

		if (riskifiedStoreDomain) {
			providers.push({provider: FraudProvider.RISKIFIED, storeDomain: riskifiedStoreDomain});
		}

		loadState = {key: loadKey, status: 'loading'};

		let autoInitFailed = false;

		const handleSignifydScriptError = (event: Event) => {
			const detail: string = (event as CustomEvent).detail || '';

			if (detail.includes('No session ID found')) {
				autoInitFailed = true;
			}
		};

		const removeScriptErrorListener = () => document.removeEventListener('sigScriptError', handleSignifydScriptError);

		const handleFailure = () => {
			removeScriptErrorListener();

			if (loadState.key === loadKey) {
				loadState.status = 'idle';
			}

			console.error('[Payrails Fraud SDK] failed to load');
		};

		const handleSuccess = () => {
			removeScriptErrorListener();

			if (loadState.key === loadKey) {
				loadState.status = 'loaded';
			}

			const sessionId = orderSessionIdRef.current;
			const api = getSignifydApi();

			if (autoInitFailed && sessionId && api) {
				api.init({sessionId});
				api.pageView();
			}
		};

		document.addEventListener('sigScriptError', handleSignifydScriptError);

		try {
			void FraudSDK.load(providers).then(handleSuccess, handleFailure);

			if (riskifiedStoreDomain) {
				removeStaleRiskifiedScripts(riskifiedStoreDomain);
			}
		} catch {
			handleFailure();
		}

		return removeScriptErrorListener;
	}, [
		basket.loaded,
		contextAvailable,
		loadKey,
		orderSessionId,
		payrailsRiskSDKActive,
		riskifiedDomain,
		riskifiedSessionId,
		riskifiedStoreDomain,
	]);
};

export default usePayrailsFraudSDK;
